import { Trans } from '@lingui/react/macro';
import { TokenResult } from '@stripe/stripe-js';

import Box from '@/design_system/Box';
import Button from '@/design_system/Button';
import Stack from '@/design_system/Stack';
import InlineText from '@/design_system/Typography/InlineText';
import Paragraph from '@/design_system/Typography/Paragraph';
import Title from '@/design_system/Typography/Title';
import IconCheck from '@/icons/Check.svg';
import IconClock from '@/icons/Clock.svg';
import { CompanyFromGetMany, useCompanies } from '@/models/company';
import {
  useGetOnboardingLink,
  useSetupDebitPaymentLink,
  useStripe,
  useStripeAccount,
} from '@/services/stripe';

export const Companies = () => {
  const { data: { companies } = {} } = useCompanies({ traits: ['aftersales'] });

  if (!companies) {
    return (
      <Paragraph>
        <Trans id="_general.loading">Loading...</Trans>
      </Paragraph>
    );
  }

  return (
    <Stack gap="24px">
      <Title size="200" as="h2">
        <Trans id="settings.companies.title">Companies</Trans>
      </Title>
      <Stack gap="8px">
        {companies.map((company) => (
          <Company key={company.id} company={company} />
        ))}
      </Stack>
    </Stack>
  );
};

const Company = ({ company }: { company: CompanyFromGetMany }) => {
  const { data: { id, accountStatus, setupIntentStatus } = {} } = useStripeAccount({
    companyId: company.id,
  });

  return (
    <Box>
      <Stack row gap="8px" justifyContent="space-between" alignItems="center">
        <InlineText weight="medium">{company.name}</InlineText>
        {accountStatus === 'complete' && setupIntentStatus === 'succeeded' && (
          <Stack row gap="8px" alignItems="center">
            <IconCheck />
            <Paragraph>
              <Trans id="settings.companies.status.ready">Stripe Account: Ready</Trans>
            </Paragraph>
          </Stack>
        )}
        {accountStatus === 'complete' && setupIntentStatus !== 'succeeded' && (
          <SetupDebitPayments company={company} />
        )}
        {accountStatus === 'pending' && (
          <Stack row gap="8px" alignItems="center">
            <IconClock />
            <Paragraph>
              <Trans id="settings.companies.status.pending">
                Stripe Account: Pending verification
              </Trans>
            </Paragraph>
          </Stack>
        )}
        {(accountStatus === 'none' || accountStatus === 'onboarding') && (
          <StartOnboarding company={company} stripeId={id} />
        )}
      </Stack>
    </Box>
  );
};

const StartOnboarding = ({
  company,
  stripeId,
}: {
  company: CompanyFromGetMany;
  stripeId?: string;
}) => {
  const stripe = useStripe();
  const { mutateAsync: getOnboardingLink, isPending: isGetOnboardingLinkPending } =
    useGetOnboardingLink();

  return (
    <Button
      variant="primary"
      isLoading={!stripe || isGetOnboardingLinkPending}
      onPress={async () => {
        let stripeAccount: TokenResult | undefined = undefined;

        if (!stripeId) {
          stripeAccount = await stripe!.createToken('account', {
            business_type: 'company',
            tos_shown_and_accepted: true,
          });
        }

        const { url } = await getOnboardingLink({
          companyId: company.id,
          accountTokenId: stripeAccount?.token?.id,
        });

        window.location.href = url;
      }}
    >
      <Trans id="settings.companies.status.start-onboarding">
        Stripe Account: Start onboarding
      </Trans>
    </Button>
  );
};

function SetupDebitPayments({ company }: { company: CompanyFromGetMany }) {
  const stripe = useStripe();
  const { mutateAsync: setupDebitPaymentLink, isPending: isSetupDebitPaymentLinkPending } =
    useSetupDebitPaymentLink();

  return (
    <Button
      variant="primary"
      isLoading={!stripe || isSetupDebitPaymentLinkPending}
      onPress={async () => {
        const { url } = await setupDebitPaymentLink({
          companyId: company.id,
        });

        window.location.href = url;
      }}
    >
      <Trans id="settings.companies.status.finish-onboarding">
        Stripe Account: Finish onboarding
      </Trans>
    </Button>
  );
}
